import { useEffect, useRef, useState } from 'react'
import { create } from 'zustand'

import ProfessionalAppFrameHeader from 'src/features/navigation/components/pro-app-frame-header'
import { NotificationModalsProvider } from 'src/features/notifications/context/notification-modals-context'
import useBreakpoint from 'src/shared/hooks/use-breakpoint'
import { cn } from 'src/shared/utils/cn'
import { ScrollBarTailwindClassesEnterprise } from 'src/shared/utils/tailwind-styles'

interface AppWindowFrameProps {
  children: React.ReactNode
}

interface AppWindowInsetsContextProps {
  insets: {
    top: number
    right: number
    bottom: number
    left: number
  }
  setInsets: (insets: { top: number; right: number; bottom: number; left: number }) => void
}

interface AppWindowScrollContainerStore {
  appWindowScrollContainer: HTMLDivElement | null
  setAppWindowScrollContainer: (container: HTMLDivElement | null) => void
}

interface AppWindowFlowShellStore {
  leftPanelContent: React.ReactNode | null
  leftPanelWidth: number
  headerHeight: number
  setFlowShell: (params: { content: React.ReactNode; width?: number; headerHeight?: number }) => void
  clearFlowShell: () => void
}

/** Used to get the insets/padding around the content of the page. */
export const useAppWindowInsetsStore = create<AppWindowInsetsContextProps>()((set) => ({
  insets: { top: 0, right: 0, bottom: 0, left: 0 },
  setInsets: (insets: { top: number; right: number; bottom: number; left: number }) => set({ insets }),
}))

/** Used to access the scrollable content container. */
export const useAppWindowScrollStore = create<AppWindowScrollContainerStore>()((set) => ({
  appWindowScrollContainer: null,
  setAppWindowScrollContainer: (container) => set({ appWindowScrollContainer: container }),
}))

export const useAppWindowFlowShellStore = create<AppWindowFlowShellStore>()((set) => ({
  leftPanelContent: null,
  leftPanelWidth: 0,
  headerHeight: 70,
  setFlowShell: ({ content, width = 100, headerHeight = 20 }) =>
    set({
      leftPanelContent: content,
      leftPanelWidth: width,
      headerHeight,
    }),
  clearFlowShell: () =>
    set({
      leftPanelContent: null,
      leftPanelWidth: 0,
      headerHeight: 75,
    }),
}))

export default function AppWindowFrameBusiness({ children }: AppWindowFrameProps) {
  const { setInsets } = useAppWindowInsetsStore()
  const { setAppWindowScrollContainer } = useAppWindowScrollStore()
  const { leftPanelContent, leftPanelWidth, headerHeight } = useAppWindowFlowShellStore()
  const { isSmTabletOrSmaller, breakpoint } = useBreakpoint()

  /** The conatiner that surrounds the content of the page. Does not include the sidebar. */
  const contentRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // const horizontalScrollRef = useHorizontalScroll<HTMLDivElement>(30)
  const sidebarRef = useRef<HTMLDivElement>(null)

  const [isSidebarHovered, setIsSidebarHovered] = useState(false)
  const isFlowMode = Boolean(leftPanelContent)
  const leftPanelTargetWidth = isFlowMode ? leftPanelWidth : 0

  // if the sidebar is hovered, we expand the sidebar
  useEffect(() => {
    const sidebar = sidebarRef.current
    if (!sidebar) {
      return
    }
    const handleMouseEnter = () => {
      setIsSidebarHovered(true)
    }
    const handleMouseLeave = () => {
      setIsSidebarHovered(false)
    }
    sidebar.addEventListener('mouseenter', handleMouseEnter)
    sidebar.addEventListener('mouseleave', handleMouseLeave)
    return () => {
      sidebar.removeEventListener('mouseenter', handleMouseEnter)
      sidebar.removeEventListener('mouseleave', handleMouseLeave)
    }
  }, [isSmTabletOrSmaller])

  useEffect(() => {
    if (contentRef.current) {
      const x = contentRef.current.getBoundingClientRect().x
      const y = contentRef.current.getBoundingClientRect().y
      const width = contentRef.current.getBoundingClientRect().width
      const height = contentRef.current.getBoundingClientRect().height

      setInsets({
        top: Math.ceil(y),
        left: Math.ceil(x),
        bottom: Math.ceil(window.innerHeight - height - y),
        right: Math.ceil(window.innerWidth - width - x),
      })
    }
  }, [isSidebarHovered, isSmTabletOrSmaller, breakpoint, setInsets, headerHeight, leftPanelTargetWidth])

  useEffect(() => {
    if (!contentRef.current) {
      return
    }
    const observer = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const x = entry.target.getBoundingClientRect().x
        const y = entry.target.getBoundingClientRect().y
        const width = entry.target.getBoundingClientRect().width
        const height = entry.target.getBoundingClientRect().height

        setInsets({
          top: Math.ceil(y),
          left: Math.ceil(x),
          bottom: Math.ceil(window.innerHeight - height - y),
          right: Math.ceil(window.innerWidth - width - x),
        })
      }
    })
    observer.observe(contentRef.current)
    return () => {
      if (observer) {
        observer.disconnect()
      }
    }
  }, [contentRef, setInsets])

  useEffect(() => {
    setAppWindowScrollContainer(scrollContainerRef.current)
    return () => setAppWindowScrollContainer(null)
  }, [setAppWindowScrollContainer])

  return (
    <NotificationModalsProvider>
      <main
        className={cn(
          'flex max-h-screen w-full flex-col overflow-hidden bg-primary px-[20px] pb-[20px]',
          ScrollBarTailwindClassesEnterprise,
        )}
      >
        <div
          className={cn('transition-[height] duration-300', isFlowMode ? 'overflow-hidden' : 'overflow-visible')}
          style={{ height: headerHeight }}
        >
          <div className={cn('transition-opacity duration-200', isFlowMode && 'pointer-events-none opacity-0')}>
            <ProfessionalAppFrameHeader />
          </div>
        </div>
        <div className="flex w-full flex-1 overflow-hidden">
          <div
            className="overflow-hidden transition-[width,opacity,transform] duration-300 ease-out"
            style={{
              width: leftPanelTargetWidth,
              opacity: leftPanelTargetWidth > 0 ? 1 : 0,
              transform: `translateX(${leftPanelTargetWidth > 0 ? 0 : -16}px)`,
            }}
          >
            <div className="size-full bg-primary pr-4">{leftPanelContent}</div>
          </div>
          <div className="min-w-0 flex-1 overflow-hidden rounded-[25px] bg-white" ref={contentRef}>
            <div ref={scrollContainerRef} className="size-full flex-1 overflow-y-auto">
              {children}
            </div>
          </div>
        </div>
      </main>
    </NotificationModalsProvider>
  )
}
